import { useEffect, useMemo, useState } from 'react'
import { fmtDate, fmtDateTime, getJson, prettyUser, sendJson, type Pairing } from './api'
import { Badge, MemberLink, Placeholder, useJson } from './common'
import { DataGrid, type Column } from './DataGrid'

// PR B1 (2026-09-22): the live pairing boxes, the sync master, Pull from Donorfy and the Unpaired view.
// Every action here writes only to our own tables (pairings, master, history).

export type SystemName = 'ontrack' | 'donorfy' | 'gocardless'
export const SYSTEM_LABEL: Record<SystemName, string> = { ontrack: 'Ontrack', donorfy: 'Donorfy', gocardless: 'GoCardless' }

export interface SearchHit {
  system: SystemName; external_id: string; external_number: string; name: string | null; email: string | null; postcode: string | null
  address: string | null; detail: string | null; paired_to: { entity_type: string; entity_id: number; name: string | null } | null; source: 'cache' | 'donorfy-live'; score: number | null
}

// ---------------------------------------------------------------------------
//  One system's pairing box on a record
// ---------------------------------------------------------------------------
export function PairingBox({ system, entityType, entityId, pairing, blurb, onChanged, extra, master, onQueued }: {
  system: SystemName; entityType: 'member' | 'membership'; entityId: number; pairing: Pairing | undefined; blurb: string; onChanged: () => void; extra?: React.ReactNode
  master?: string | null; onQueued?: (batchId: number) => void
}) {
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<SearchHit[] | null>(null)
  const [direction, setDirection] = useState<'pull' | 'send'>(master === 'membership' ? 'send' : 'pull')
  const [creating, setCreating] = useState(false)
  const canSearch = !(system === 'ontrack' && pairing)  // an Ontrack pairing is the record's own origin
  const canSend = entityType === 'member' && system !== 'gocardless'
  const canSync = Boolean(pairing) && system !== 'gocardless' && (direction === 'pull' || canSend)
  const syncTitle = !pairing ? 'Pair first.'
    : system === 'gocardless' ? 'GoCardless is pull-only and refreshed nightly; use Pull from GoCardless on the Integrations tab.'
    : direction === 'pull'
      ? (system === 'ontrack' ? 'Refresh this record from the Ontrack copy taken at the last global pull: Ontrack-only fields always; shared fields only if Ontrack is this record’s master.' : 'Read this constituent live from Donorfy (details and Gift Aid declarations) into the cache; shared fields change only if Donorfy is this record’s master.')
      : `Queue a line for every field where this record differs from ${SYSTEM_LABEL[system]} (writable fields only) in this record’s change batch — Write Now sends them.`
  // PR D (2026-09-23): Sync now — Pull from (one record, from the cache / live Donorfy) or Send To (queue lines for Write Now)
  const syncNow = async () => {
    setBusy(true); setError(null); setNote(null)
    try {
      const r = await sendJson<{ note?: string; batch_id?: number | null; queued?: number }>(`/api/membership/${entityType}/${entityId}/sync/${system}`, 'POST', { direction })
      setNote(r.note ?? 'Done.')
      if (direction === 'send' && r.batch_id && onQueued) onQueued(r.batch_id)
      onChanged()
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const create = async () => {
    setBusy(true); setError(null); setNote(null)
    try {
      const r = await sendJson<{ constituent: { number: number } }>(`/api/membership/members/${entityId}/create/donorfy`, 'POST', {})
      setCreating(false); setNote(`Created Donorfy constituent ${r.constituent.number} and paired it to this member.`); onChanged()
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }

  const search = async (text: string) => {
    setBusy(true); setError(null)
    try {
      const p = new URLSearchParams({ system, q: text, entity_type: entityType })
      if (entityType === 'member') p.set('member_id', String(entityId))
      const r = await getJson<{ hits: SearchHit[] }>(`/api/membership/pairing/search?${p.toString()}`)
      setHits(r.hits ?? [])
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const pair = async (h: SearchHit) => {
    setBusy(true); setError(null)
    try {
      await sendJson(`/api/membership/${entityType}/${entityId}/pair`, 'POST', { system, external_id: h.external_id, external_number: h.external_number })
      setSearching(false); setHits(null); setQ(''); onChanged()
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const unpair = async () => {
    setBusy(true); setError(null)
    try { await sendJson(`/api/membership/${entityType}/${entityId}/pair/${system}`, 'DELETE'); onChanged() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }

  return (
    <div className={'mbr-pairing ' + (pairing ? 'mbr-pairing--paired' : 'mbr-pairing--unpaired')}>
      <h4>{SYSTEM_LABEL[system]} {pairing ? <Badge kind="ok">Paired</Badge> : <Badge kind="warn">No pairing</Badge>}</h4>
      {pairing ? (
        <>
          <div className="mbr-pairing-id">{pairing.external_number && pairing.external_number !== pairing.external_id ? `${pairing.external_number} · ` : ''}{pairing.external_id}</div>
          <div className="mbr-note">Paired by {pairing.method === 'key' ? 'key' : pairing.method === 'manual' ? `hand (${prettyUser(pairing.paired_by)})` : pairing.method} · {fmtDate(pairing.paired_at)}</div>
          {extra}
        </>
      ) : <div className="mbr-note">{blurb}</div>}
      <div className="mbr-pairing-actions">
        <span className="mbr-segment" title="Pull from: take this system's values into this record (shared fields only when it is the master). Send To: queue this record's values for that system; Write Now sends them.">
          <button type="button" className={direction === 'pull' ? 'active' : ''} disabled={!pairing} onClick={() => setDirection('pull')}>Pull from</button>
          <button type="button" className={direction === 'send' ? 'active' : ''} disabled={!pairing || !canSend} onClick={() => setDirection('send')}
                  title={!canSend ? (entityType === 'membership' ? 'A membership mirrors Ontrack; there is nothing to send.' : 'GoCardless is pull-only.') : undefined}>Send To</button>
        </span>
        <button type="button" className="btn-secondary" disabled={busy || !canSync} title={syncTitle} onClick={() => void syncNow()}>{busy ? 'Working…' : 'Sync now'}</button>
        {canSearch && !searching && <button type="button" className="btn-secondary" disabled={busy} onClick={() => { setSearching(true); void search(q) }}>{pairing ? 'Re-pair…' : `Find in ${SYSTEM_LABEL[system]}…`}</button>}
        {system === 'donorfy' && !pairing && entityType === 'member' && !creating && <button type="button" className="btn-secondary" disabled={busy} onClick={() => setCreating(true)} title="Create a new constituent in Donorfy from this member (ExternalKey = person number). Only when the Donorfy writer is ON.">Create in Donorfy…</button>}
        {pairing && system !== 'ontrack' && <button type="button" className="btn-link btn-link--danger" disabled={busy} onClick={unpair}>Unpair</button>}
      </div>
      {creating && (
        <div className="mbr-toolbar">
          <span className="mbr-note">Create a new constituent in Donorfy from this member’s details, keyed by the person number? Only works while the Donorfy writer is ON.</span>
          <button type="button" className="btn-primary" disabled={busy} onClick={() => void create()}>{busy ? 'Creating…' : 'Yes, create'}</button>
          <button type="button" className="btn-link" disabled={busy} onClick={() => setCreating(false)}>Cancel</button>
        </div>
      )}
      {note && <p className="mbr-note">{note}</p>}
      {searching && (
        <div className="mbr-picker">
          <div className="mbr-toolbar">
            <label className="mbr-field mbr-field--search">Search {SYSTEM_LABEL[system]}
              <input type="search" autoFocus value={q} placeholder={system === 'donorfy' ? 'name, email, postcode, constituent no or key — blank = Donorfy’s own match for this person' : 'name, email, postcode or id'}
                     onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void search(q) }} />
            </label>
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => void search(q)}>{busy ? 'Searching…' : 'Search'}</button>
            <button type="button" className="btn-link" onClick={() => { setSearching(false); setHits(null) }}>Close</button>
          </div>
          {error && <p className="mbr-error">{error}</p>}
          {hits && hits.length === 0 && !busy && <p className="mbr-note">Nothing found{system === 'donorfy' ? ' in the nightly cache or by Donorfy’s duplicate check' : ''}. Try a surname or a postcode.</p>}
          {hits && hits.length > 0 && (
            <div className="mbr-picker-list">
              {hits.map((h) => (
                <div key={`${h.system}-${h.external_id}`} className="mbr-picker-item mbr-picker-row">
                  <div>
                    <strong>{h.name ?? '(no name)'}</strong>{h.score != null && <Badge kind="info" title="Donorfy duplicate-check score">score {h.score}</Badge>}{h.source === 'donorfy-live' && <Badge kind="info">Donorfy match</Badge>}
                    <div className="mbr-note">{[h.email, h.postcode, h.address].filter(Boolean).join(' · ')}</div>
                    <div className="mbr-note">{h.detail}{h.paired_to ? ` · already paired to ${h.paired_to.entity_type} ${h.paired_to.name ?? h.paired_to.entity_id}` : ''}</div>
                  </div>
                  <button type="button" className="btn-primary" disabled={busy || Boolean(h.paired_to && h.paired_to.entity_id !== entityId)} onClick={() => void pair(h)}>Pair</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!searching && error && <p className="mbr-error">{error}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Sync master
// ---------------------------------------------------------------------------
export function MasterSelector({ entityType, entityId, master, onChanged }: { entityType: 'member' | 'membership'; entityId: number; master: string | null; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const set = async (m: string | null) => {
    setBusy(true); setError(null)
    try { await sendJson(`/api/membership/${entityType}/${entityId}/master`, 'PUT', { master: m }); onChanged() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const opts: { key: string | null; label: string; title: string }[] = [
    { key: null, label: 'No master', title: 'Nothing is overwritten by a pull; every difference is a discrepancy for someone to decide. The default.' },
    { key: 'ontrack', label: 'Ontrack', title: 'A pull from Ontrack overwrites this record’s shared fields and (from the writer builds) queues the change for Donorfy.' },
    { key: 'donorfy', label: 'Donorfy', title: 'A pull from Donorfy overwrites this record’s shared fields and (from the writer builds) queues the change for Ontrack.' },
    { key: 'membership', label: 'Membership', title: 'This system is the master: pulls never overwrite it; its values are what the writers send.' },
  ]
  return (
    <div className="mbr-section">
      <h3>Sync master</h3>
      <div className="mbr-toolbar">
        <span className="mbr-segment">
          {opts.map((o) => <button key={o.key ?? 'none'} type="button" className={(master ?? null) === o.key ? 'active' : ''} disabled={busy} title={o.title} onClick={() => void set(o.key)}>{o.label}</button>)}
        </span>
        <span className="mbr-note">{opts.find((o) => o.key === (master ?? null))?.title}</span>
      </div>
      {error && <p className="mbr-error">{error}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Pull from Donorfy — two steps the browser drives
// ---------------------------------------------------------------------------
interface DonorfyStatus {
  configured: boolean; meta: { source: string; refreshed_at: string | null; row_count: number; status: string; message: string | null }[]
  last_pull: string | null; last_pull_status: string | null; last_pull_message: string | null; lockout_minutes: number; next_available_at: string | null; locked: boolean; can_request: boolean
}
export function DonorfyPullBlock({ onCompleted }: { onCompleted: () => void }) {
  const status = useJson<DonorfyStatus>('/api/membership/sync/donorfy')
  const [phase, setPhase] = useState<'idle' | 'refresh' | 'reconcile' | 'done' | 'error'>('idle')
  const [msg, setMsg] = useState<string | null>(null)
  // The constituent list takes 30–45 s to read; the hosting gateway may drop the call at ~45 s while the
  // function carries on. If that happens, poll the status until the pull is no longer 'running'.
  const waitForRefresh = async (): Promise<number | null> => {
    for (let i = 0; i < 24; i++) {
      await new Promise((r) => setTimeout(r, 5000))
      const st = await getJson<DonorfyStatus>('/api/membership/sync/donorfy').catch(() => null)
      if (st && st.last_pull_status && st.last_pull_status !== 'running') {
        if (st.last_pull_status === 'ok') return st.meta.find((m) => m.source === 'donorfy-pull')?.row_count ?? null
        throw new Error(st.last_pull_message ?? 'The Donorfy pull failed')
      }
      setMsg(`Still reading the constituent list from Donorfy… (${(i + 1) * 5 + 45} s)`)
    }
    throw new Error('The Donorfy pull did not finish within two minutes; try again in ten minutes.')
  }
  const run = async () => {
    setPhase('refresh'); setMsg('Reading the constituent list from Donorfy (about 40 seconds)…')
    try {
      let rows: number | null = null
      try {
        const r1 = await sendJson<{ rows: number }>('/api/membership/sync/donorfy', 'POST', { step: 'refresh' })
        rows = r1.rows
      } catch (e) {
        const text = String((e as Error)?.message ?? e)
        if (/HTTP 5\d\d|Backend call failure|timed out|Failed to fetch/i.test(text)) { setMsg('The gateway dropped the call while Donorfy was still answering — waiting for the pull to finish…'); rows = await waitForRefresh() }
        else throw e
      }
      setPhase('reconcile'); setMsg(`${(rows ?? 0).toLocaleString('en-GB')} constituents refreshed. Pairing by person number and re-checking every record…`)
      const r2 = await sendJson<{ paired: number; pulled: number }>('/api/membership/sync/donorfy', 'POST', { step: 'reconcile' })
      setPhase('done'); setMsg(`Done: ${r2.paired.toLocaleString('en-GB')} new pairing${r2.paired === 1 ? '' : 's'} by key${r2.pulled ? `, ${r2.pulled} field${r2.pulled === 1 ? '' : 's'} pulled for Donorfy-master records` : ''}.`)
      status.reload(); onCompleted()
    } catch (e) { setPhase('error'); setMsg(String((e as Error)?.message ?? e)); status.reload() }
  }
  const s = status.data
  const busy = phase === 'refresh' || phase === 'reconcile'
  return (
    <div className="mbr">
      <div className="mbr-sync">
        <button type="button" className="btn-primary" disabled={busy || !s || !s.can_request || !s.configured} onClick={run}
                title="Read every Donorfy constituent again (the same list the Fundraising tab refreshes nightly), pair members by Donorfy’s ExternalKey = Ontrack person number, and re-check every record’s differences. Nothing is written to Donorfy.">
          {busy ? 'Pulling from Donorfy…' : 'Pull from Donorfy now'}
        </button>
        <span className="mbr-status">
          {msg && <>{msg} </>}
          {!msg && s?.last_pull && <>Last pull {fmtDateTime(s.last_pull)} UTC{s.last_pull_message ? ` — ${s.last_pull_message}` : ''}{s.last_pull_status === 'error' ? ' — ✗ failed' : ''}. </>}
          {s && !busy && (s.locked && s.next_available_at ? <>Available again at {fmtDateTime(s.next_available_at)}.</> : s.configured ? <>Available to run now.</> : <>Donorfy is not configured on this host.</>)}
          {phase === 'error' && <span className="mbr-error"> Pull failed.</span>}
        </span>
      </div>
      <div className="mbr-note">The nightly Fundraising refresh (05:30) keeps the same constituent list current; this button re-reads it on demand and re-pairs.</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
//  The Unpaired view (R28)
// ---------------------------------------------------------------------------
type UnpairedView = 'members-donorfy' | 'donorfy-members' | 'members-gocardless'
interface Suggestion { kind: string; constituent_number?: number; constituent_id?: string | null; member_id?: number; customer_id?: string; name: string | null; email: string | null; postcode: string | null; external_key?: string | null; membership_card_number?: string | null; status?: string; same_surname?: number }
interface UnpairedRow extends Record<string, unknown> { suggestions: Suggestion[] }
interface UnpairedPayload { view: UnpairedView; rows: UnpairedRow[]; total: number; page: number; size: number }
interface UnpairedSummary { members_no_donorfy: number; members_no_donorfy_all: number; donorfy_no_member: number; donorfy_no_member_trust: number; members_no_gocardless: number; donorfy_paired: number; donorfy_constituents: number; donorfy_keyed: number }

const KIND_LABEL: Record<string, string> = { key: 'same person number', email: 'same email', 'name+postcode': 'same surname and postcode', membership: 'the membership’s direct debit customer' }

export function UnpairedView({ onChanged }: { onChanged: () => void }) {
  const [view, setView] = useState<UnpairedView>('members-donorfy')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(30)
  const [q, setQ] = useState('')
  const [draft, setDraft] = useState('')
  const [inactive, setInactive] = useState(false)
  const [trust, setTrust] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)
  useEffect(() => { const id = setTimeout(() => setQ(draft), 350); return () => clearTimeout(id) }, [draft])
  const summary = useJson<UnpairedSummary>(`/api/membership/unpaired/summary?k=${reloadKey}`)
  const url = useMemo(() => {
    const p = new URLSearchParams({ view, page: String(page), size: String(size) })
    if (q) p.set('q', q); if (inactive) p.set('inactive', '1'); if (view === 'donorfy-members' && trust) p.set('trust', '1')
    p.set('k', String(reloadKey))
    return `/api/membership/unpaired?${p.toString()}`
  }, [view, page, size, q, inactive, trust, reloadKey])
  const data = useJson<UnpairedPayload>(url)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const changed = () => { setReloadKey((k) => k + 1); onChanged() }

  const pairMember = async (memberId: number, system: 'donorfy' | 'gocardless', externalId: string, externalNumber: string, key: string) => {
    setBusyKey(key); setError(null)
    try { await sendJson(`/api/membership/member/${memberId}/pair`, 'POST', { system, external_id: externalId, external_number: externalNumber }); changed() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusyKey(null) }
  }
  const s = summary.data
  const sugCell = (r: UnpairedRow, render: (sg: Suggestion, i: number) => React.ReactNode) => (
    <div className="mbr-suggestions">
      {r.suggestions.length === 0 && <span className="mbr-note">no match found — use Find on the record</span>}
      {r.suggestions.map((sg, i) => <div key={i} className="mbr-suggestion">{render(sg, i)}</div>)}
    </div>
  )

  const memberDonorfyCols: Column<UnpairedRow>[] = [
    { key: 'full_name', label: 'Member', sortable: false, render: (r) => <MemberLink id={Number(r.member_id)}>{String(r.full_name ?? '')}</MemberLink> },
    { key: 'membership_card_number', label: 'Card no', sortable: false },
    { key: 'category', label: 'Membership', sortable: false },
    { key: 'email', label: 'Email', sortable: false, className: 'wrap' },
    { key: 'postcode', label: 'Postcode', sortable: false },
    { key: 'status', label: 'Status', sortable: false },
    { key: 'suggestions', label: 'Donorfy match', sortable: false, className: 'wrap', render: (r) => sugCell(r, (sg) => (
      <>
        <span><strong>{sg.name ?? `constituent ${sg.constituent_number}`}</strong> <span className="mbr-note">#{sg.constituent_number} · {KIND_LABEL[sg.kind] ?? sg.kind}{sg.email ? ` · ${sg.email}` : ''}{sg.postcode ? ` · ${sg.postcode}` : ''}</span>{sg.same_surname === 0 && <> <Badge kind="warn" title="The surname differs — a shared household email or address, not necessarily the same person.">different surname</Badge></>}</span>
        <button type="button" className="btn-secondary mbr-pbtn" disabled={busyKey !== null} onClick={() => void pairMember(Number(r.member_id), 'donorfy', String(sg.constituent_id ?? sg.constituent_number), String(sg.constituent_number), `${r.member_id}-${sg.constituent_number}`)}>Pair</button>
      </>
    )) },
  ]
  const donorfyMemberCols: Column<UnpairedRow>[] = [
    { key: 'display_name', label: 'Donorfy constituent', sortable: false, render: (r) => <><strong>{String(r.display_name ?? '')}</strong> <span className="mbr-note">#{String(r.constituent_number)}</span>{r.trust_tag ? <> <Badge kind="info">Trust Membership tag</Badge></> : null}</> },
    { key: 'external_key', label: 'Key', sortable: false },
    { key: 'email', label: 'Email', sortable: false, className: 'wrap' },
    { key: 'postcode', label: 'Postcode', sortable: false },
    { key: 'date_added', label: 'Added', sortable: false, render: (r) => fmtDate(r.date_added as string | null) },
    { key: 'suggestions', label: 'Member match', sortable: false, className: 'wrap', render: (r) => sugCell(r, (sg) => (
      <>
        <span><MemberLink id={sg.member_id ?? null}><strong>{sg.name ?? `member ${sg.member_id}`}</strong></MemberLink> <span className="mbr-note">{sg.membership_card_number ?? ''} · {KIND_LABEL[sg.kind] ?? sg.kind}{sg.status ? ` · ${sg.status}` : ''}</span>{sg.same_surname === 0 && <> <Badge kind="warn" title="The surname differs — a shared household email or address, not necessarily the same person.">different surname</Badge></>}</span>
        <button type="button" className="btn-secondary mbr-pbtn" disabled={busyKey !== null} onClick={() => void pairMember(Number(sg.member_id), 'donorfy', String(r.constituent_id ?? r.constituent_number), String(r.constituent_number), `${sg.member_id}-${r.constituent_number}`)}>Pair</button>
      </>
    )) },
  ]
  const memberGcCols: Column<UnpairedRow>[] = [
    { key: 'full_name', label: 'Member', sortable: false, render: (r) => <MemberLink id={Number(r.member_id)}>{String(r.full_name ?? '')}</MemberLink> },
    { key: 'membership_card_number', label: 'Card no', sortable: false },
    { key: 'category', label: 'Membership', sortable: false },
    { key: 'email', label: 'Email', sortable: false, className: 'wrap' },
    { key: 'suggestions', label: 'GoCardless customer', sortable: false, className: 'wrap', render: (r) => sugCell(r, (sg) => (
      <>
        <span><strong>{sg.name ?? sg.customer_id}</strong> <span className="mbr-note">{sg.customer_id} · {KIND_LABEL[sg.kind] ?? sg.kind}{sg.email ? ` · ${sg.email}` : ''}</span></span>
        <button type="button" className="btn-secondary mbr-pbtn" disabled={busyKey !== null} onClick={() => void pairMember(Number(r.member_id), 'gocardless', String(sg.customer_id), String(sg.customer_id), `${r.member_id}-${sg.customer_id}`)}>Pair</button>
      </>
    )) },
  ]
  const columns = view === 'members-donorfy' ? memberDonorfyCols : view === 'donorfy-members' ? donorfyMemberCols : memberGcCols
  const rowKey = (r: UnpairedRow) => (view === 'donorfy-members' ? `c${r.constituent_number}` : `m${r.member_id}`)

  return (
    <div className="mbr">
      <p className="mbr-blurb">Pair everything that already exists before anything is created anywhere. Donorfy is keyed to Ontrack by person number; where the key is missing, the suggestions match on email, then surname and postcode. A shared household email can point at the spouse — a different surname is flagged — so check before you press Pair.</p>
      {s && (
        <div className="mbr-kpis">
          <div className="mbr-kpi"><div className="mbr-kpi-label">Members paired to Donorfy</div><div className="mbr-kpi-value">{s.donorfy_paired.toLocaleString('en-GB')}</div><div className="mbr-kpi-sub">of {s.donorfy_keyed.toLocaleString('en-GB')} keyed constituents ({s.donorfy_constituents.toLocaleString('en-GB')} in all)</div></div>
          <div className="mbr-kpi"><div className="mbr-kpi-label">Current members without Donorfy</div><div className="mbr-kpi-value">{s.members_no_donorfy.toLocaleString('en-GB')}</div><div className="mbr-kpi-sub">{s.members_no_donorfy_all.toLocaleString('en-GB')} including inactive</div></div>
          <div className="mbr-kpi"><div className="mbr-kpi-label">Donorfy people without a member</div><div className="mbr-kpi-value">{s.donorfy_no_member.toLocaleString('en-GB')}</div><div className="mbr-kpi-sub">{s.donorfy_no_member_trust.toLocaleString('en-GB')} carry a Trust Membership tag</div></div>
          <div className="mbr-kpi"><div className="mbr-kpi-label">Members without GoCardless</div><div className="mbr-kpi-value">{s.members_no_gocardless.toLocaleString('en-GB')}</div><div className="mbr-kpi-sub">where a customer can be suggested</div></div>
        </div>
      )}
      <div className="mbr-pills" role="tablist">
        {([['members-donorfy', 'Members without Donorfy', s?.members_no_donorfy], ['donorfy-members', 'Donorfy without a member', s?.donorfy_no_member], ['members-gocardless', 'Members without GoCardless', s?.members_no_gocardless]] as [UnpairedView, string, number | undefined][]).map(([k, label, n]) => (
          <button key={k} type="button" role="tab" aria-selected={view === k} className={'mbr-pill mbr-pill--sub' + (view === k ? ' active' : '')} onClick={() => { setView(k); setPage(1) }}>{label}{n !== undefined && <span className="mbr-pill-n">{n.toLocaleString('en-GB')}</span>}</button>
        ))}
      </div>
      <div className="mbr-toolbar">
        <label className="mbr-field mbr-field--search">Search<input type="search" value={draft} onChange={(e) => { setDraft(e.target.value); setPage(1) }} placeholder="name, email, postcode, card no or key" /></label>
        <label className="mbr-check"><input type="checkbox" checked={inactive} onChange={(e) => { setInactive(e.target.checked); setPage(1) }} /> Include inactive</label>
        {view === 'donorfy-members' && <label className="mbr-check"><input type="checkbox" checked={trust} onChange={(e) => { setTrust(e.target.checked); setPage(1) }} /> Trust Membership tag only</label>}
      </div>
      {error && <p className="mbr-error">{error}</p>}
      <DataGrid<UnpairedRow> columns={columns} rows={data.data?.rows ?? []} total={data.data?.total ?? 0} page={page} size={size} sort="" dir="asc"
        loading={data.loading} error={data.error} rowKey={rowKey} onSort={() => undefined} onPage={setPage} onSize={(n) => { setSize(n); setPage(1) }}
        emptyText={view === 'donorfy-members' && trust ? 'Every Trust-tagged Donorfy person is paired. Untick the tag filter to see the rest.' : 'Nothing unpaired here.'} />
      {!s && !summary.loading && <Placeholder title="Counts unavailable">{summary.error ?? 'no summary'}</Placeholder>}
    </div>
  )
}
